<xemex-dialog-shell [title]="'locations.importDialog.title' | translate">
  <div class="import-dialog">
    <p class="import-dialog__subtitle">{{ 'locations.importDialog.subtitle' | translate }}</p>

    <!-- Step indicator -->
    <ol class="import-dialog__steps">
      <li class="import-dialog__step" [attr.data-state]="stepState(1)">
        <span class="import-dialog__marker">
          @if (stepState(1) === 'done') {
            <mat-icon>check</mat-icon>
          } @else {
            1
          }
        </span>
        <span class="import-dialog__step-label">
          {{ 'locations.importDialog.step.upload' | translate }}
        </span>
      </li>
      <span
        class="import-dialog__connector"
        [attr.data-state]="reviewFailed() && step() === 2 ? 'error' : stepState(2)"
      ></span>
      <li
        class="import-dialog__step"
        [attr.data-state]="reviewFailed() && step() === 2 ? 'error' : stepState(2)"
      >
        <span class="import-dialog__marker">
          @if (reviewFailed() && step() === 2) {
            <mat-icon>priority_high</mat-icon>
          } @else if (stepState(2) === 'done') {
            <mat-icon>check</mat-icon>
          } @else {
            2
          }
        </span>
        <span class="import-dialog__step-label">
          {{ 'locations.importDialog.step.review' | translate }}
        </span>
      </li>
      <span class="import-dialog__connector" [attr.data-state]="stepState(3)"></span>
      <li class="import-dialog__step" [attr.data-state]="stepState(3)">
        <span class="import-dialog__marker">
          @if (step() === 3) {
            <mat-icon>check</mat-icon>
          } @else {
            3
          }
        </span>
        <span class="import-dialog__step-label">
          {{ 'locations.importDialog.step.done' | translate }}
        </span>
      </li>
    </ol>

    <!-- Step content -->
    @switch (step()) {
      @case (1) {
        <div class="import-dialog__pane">
          <p class="import-dialog__desc">{{ 'locations.importDialog.upload.desc' | translate }}</p>

          @if (requiresPartnerSelection()) {
            <div>
              <xemex-partner-select
                [formControl]="partnerControl"
                [label]="'locations.importDialog.upload.partnerLabel' | translate"
                [placeholder]="'locations.importDialog.upload.partnerPlaceholder' | translate"
              />
              @if (partnerError()) {
                <p class="import-dialog__inline-error">
                  {{ 'locations.importDialog.upload.noPartner' | translate }}
                </p>
              }
            </div>
          }

          @if (canManageInstallationGroups()) {
            <xemex-select-field
              [label]="'locations.importDialog.upload.installationGroupsLabel' | translate"
            >
              <mat-select
                [formControl]="installationGroupControl"
                multiple
                [placeholder]="
                  'locations.importDialog.upload.installationGroupsPlaceholder' | translate
                "
              >
                @for (group of installationGroups(); track group.installationGroupOid) {
                  <mat-option [value]="group.installationGroupOid">{{ group.name }}</mat-option>
                }
              </mat-select>
            </xemex-select-field>
          }

          <div
            class="import-dialog__dropzone"
            [class.import-dialog__dropzone--error]="!!fileError()"
            xemexFilesDrop
            [acceptedTypes]="acceptedTypes"
            (dropped)="onFileDropped($event)"
            (click)="fileInput.click()"
          >
            <input
              #fileInput
              type="file"
              hidden
              accept=".csv"
              xemexBrowseFile
              (fileSelected)="onFileSelected($event)"
            />
            @if (selectedFile(); as file) {
              <mat-icon class="import-dialog__dropzone-icon">description</mat-icon>
              <span class="import-dialog__dropzone-file">{{ file.name }}</span>
              <button
                type="button"
                class="import-dialog__dropzone-clear"
                (click)="clearFile(); $event.stopPropagation()"
              >
                {{ 'locations.importDialog.upload.changeFile' | translate }}
              </button>
            } @else {
              <mat-icon class="import-dialog__dropzone-icon">upload_file</mat-icon>
              <span class="import-dialog__dropzone-title">
                {{ 'locations.importDialog.upload.dropTitle' | translate }}
              </span>
              <span class="import-dialog__dropzone-hint">
                {{ 'locations.importDialog.upload.dropHint' | translate }}
              </span>
            }
          </div>

          @if (fileError() === 'tooLarge') {
            <p class="import-dialog__inline-error">
              {{ 'locations.importDialog.upload.tooLarge' | translate }}
            </p>
          } @else if (fileError() === 'wrongType') {
            <p class="import-dialog__inline-error">
              {{ 'locations.importDialog.upload.wrongType' | translate }}
            </p>
          }

          <div class="import-dialog__template">
            <mat-icon>description</mat-icon>
            <span>{{ 'locations.importDialog.upload.templatePrompt' | translate }}</span>
            <a [href]="templateHref" download>
              {{ 'locations.importDialog.upload.templateLink' | translate }}
            </a>
          </div>
        </div>
      }

      @case (2) {
        <div class="import-dialog__pane">
          @if (isValidating()) {
            <div class="import-dialog__loading">
              <mat-icon class="import-dialog__spinner">progress_activity</mat-icon>
              <span>{{ 'locations.importDialog.review.validating' | translate }}</span>
            </div>
          } @else if (reviewFailed()) {
            <div class="import-dialog__error">
              <div class="import-dialog__error-icon"><mat-icon>error</mat-icon></div>
              <div class="import-dialog__error-body">
                <h3 class="import-dialog__error-title">
                  {{ 'locations.importDialog.error.title' | translate }}
                </h3>
                <p class="import-dialog__error-text">
                  {{
                    'locations.importDialog.error.couldNotRead'
                      | translate: { file: $safeNavigationMigration(selectedFile()?.name) }
                  }}
                </p>
                <p class="import-dialog__error-text">
                  {{ 'locations.importDialog.error.requiredColumns' | translate }}
                </p>
                <p class="import-dialog__error-text">
                  {{ 'locations.importDialog.error.fixHint' | translate }}
                </p>
              </div>
            </div>
          } @else {
            <div class="import-dialog__review-head">
              @if (allValid()) {
                <xemex-status-chip
                  status="success"
                  [label]="
                    'locations.importDialog.review.readyChip'
                      | translate: { count: reviewRows().length }
                  "
                />
              } @else {
                <xemex-status-chip
                  status="error"
                  [label]="
                    'locations.importDialog.review.invalidChip'
                      | translate: { count: invalidCount() }
                  "
                />
              }
            </div>
            <p class="import-dialog__desc">
              @if (allValid()) {
                {{
                  'locations.importDialog.review.passed' | translate: { count: reviewRows().length }
                }}
              } @else {
                {{
                  'locations.importDialog.review.someInvalid'
                    | translate: { invalid: invalidCount(), total: reviewRows().length }
                }}
              }
            </p>
            <div class="import-dialog__preview">
              <p class="import-dialog__preview-title">
                {{ 'locations.importDialog.review.previewTitle' | translate }}
              </p>
              <ul class="import-dialog__preview-list">
                @for (row of reviewRows(); track $index) {
                  <li
                    class="import-dialog__preview-item"
                    [class.import-dialog__preview-item--invalid]="!row.valid"
                  >
                    <mat-icon [class.import-dialog__preview-icon--invalid]="!row.valid">{{
                      row.valid ? 'location_on' : 'error'
                    }}</mat-icon>
                    <div>
                      <div class="import-dialog__preview-line1">{{ previewLine1(row.raw) }}</div>
                      <div class="import-dialog__preview-line2">{{ previewLine2(row.raw) }}</div>
                      @if (!row.valid) {
                        <div class="import-dialog__preview-status">{{ row.status }}</div>
                      }
                    </div>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      }

      @case (3) {
        <div class="import-dialog__pane">
          <div class="import-dialog__review-head">
            <xemex-status-chip
              status="success"
              [label]="
                'locations.importDialog.done.importedChip'
                  | translate: { count: createdLocations().length }
              "
            />
          </div>
          <p class="import-dialog__desc">{{ 'locations.importDialog.done.desc' | translate }}</p>
          <div class="import-dialog__preview">
            <p class="import-dialog__preview-title">
              {{ 'locations.importDialog.done.listTitle' | translate }}
            </p>
            <ul class="import-dialog__preview-list">
              @for (loc of createdLocations(); track loc.id) {
                <li class="import-dialog__preview-item">
                  <mat-icon>location_on</mat-icon>
                  <div>
                    <a
                      class="import-dialog__created-link"
                      [routerLink]="['/locations', loc.id]"
                      (click)="done()"
                      >{{ loc.line1 }}</a
                    >
                    <div class="import-dialog__preview-line2">{{ loc.line2 }}</div>
                  </div>
                </li>
              }
            </ul>
          </div>
        </div>
      }
    }
  </div>

  <div actions class="import-dialog__actions">
    @switch (step()) {
      @case (1) {
        <xemex-text-button type="button" intent="primary" (click)="cancel()">
          {{ 'general.btnCancel' | translate }}
        </xemex-text-button>
        <xemex-filled-button
          type="button"
          icon="arrow_forward"
          iconPosition="end"
          [disabled]="!selectedFile() || (requiresPartnerSelection() && !selectedPartnerOid())"
          (click)="validateAndReview()"
        >
          @if (selectedFile()) {
            {{ 'locations.importDialog.btnContinue' | translate }}
          } @else {
            {{ 'locations.importDialog.upload.selectToContinue' | translate }}
          }
        </xemex-filled-button>
      }
      @case (2) {
        <xemex-text-button
          type="button"
          intent="primary"
          icon="arrow_back"
          (click)="backToUpload()"
        >
          {{ 'locations.importDialog.btnBack' | translate }}
        </xemex-text-button>
        <xemex-filled-button
          type="button"
          icon="file_upload"
          [loading]="isImporting()"
          [disabled]="isValidating() || reviewFailed() || !allValid()"
          (click)="confirmImport()"
        >
          @if (allValid()) {
            {{ 'locations.importDialog.review.import' | translate: { count: reviewRows().length } }}
          } @else {
            {{ 'locations.importDialog.review.importShort' | translate }}
          }
        </xemex-filled-button>
      }
      @case (3) {
        <span></span>
        <xemex-filled-button type="button" icon="check" (click)="done()">
          {{ 'locations.importDialog.done.btnDone' | translate }}
        </xemex-filled-button>
      }
    }
  </div>
</xemex-dialog-shell>
